import { useMemo } from 'react';
import { Map, Rocket, CheckCircle2, Loader2 } from 'lucide-react';
import { RoadmapTimeline } from './RoadmapTimeline';
import { ROADMAP_MILESTONES } from '../data';

/**
 * Renders the roadmap timeline + stats. Used both standalone (RoadmapPage)
 * and embedded inside the Home page Roadmap tab.
 */
export function RoadmapView() {
  const stats = useMemo(() => {
    const live = ROADMAP_MILESTONES.filter((m) => m.status === 'live').length;
    const inProgress = ROADMAP_MILESTONES.reduce(
      (acc, m) =>
        acc +
        (m.status === 'in-progress' ? 1 : 0) +
        m.submodules.filter((s) => s.status === 'in-progress').length,
      0
    );
    return { total: ROADMAP_MILESTONES.length, live, inProgress };
  }, []);

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <section className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-card via-capitol-red-50/40 to-card p-6 sm:p-8">
        <span
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-capitol-red-500/10 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-warning/10 blur-3xl"
        />

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-capitol-red-100 bg-card/80 px-2.5 py-0.5 text-xs font-medium text-capitol-red-500 shadow-sm backdrop-blur">
              <Rocket className="h-3 w-3" />
              DOP Roadmap
            </span>
            <h1 className="text-3xl font-bold tracking-tight text-capitol-gray-500 sm:text-4xl">
              Where the platform is,{' '}
              <span className="bg-gradient-to-r from-capitol-red-500 to-capitol-red-600 bg-clip-text text-transparent">
                where it's going.
              </span>
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Each milestone groups the modules that shipped together. Click any milestone
              to see what's inside.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <StatPill
              icon={<Map className="h-4 w-4" />}
              value={stats.total}
              label="Milestones"
              tone="neutral"
            />
            <StatPill
              icon={<CheckCircle2 className="h-4 w-4" />}
              value={stats.live}
              label="Live"
              tone="live"
            />
            <StatPill
              icon={<Loader2 className="h-4 w-4 animate-spin" />}
              value={stats.inProgress}
              label="Active"
              tone="active"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl">
        <RoadmapTimeline milestones={ROADMAP_MILESTONES} />
      </div>
    </div>
  );
}

interface StatPillProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  tone: 'neutral' | 'live' | 'active';
}

function StatPill({ icon, value, label, tone }: StatPillProps) {
  const toneClasses = {
    neutral: 'border-capitol-gray-200 text-capitol-gray-500',
    live: 'border-capitol-red-200 text-capitol-red-500 bg-capitol-red-50/40',
    active: 'border-warning/30 text-warning bg-warning/10',
  }[tone];

  return (
    <div
      className={`flex items-center gap-2 rounded-lg border bg-card/70 px-3 py-2 shadow-sm backdrop-blur ${toneClasses}`}
    >
      {icon}
      <div className="leading-tight">
        <div className="text-lg font-bold">{value}</div>
        <div className="text-[10px] font-medium uppercase tracking-wider opacity-80">
          {label}
        </div>
      </div>
    </div>
  );
}
